import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2, LogOut, ScanLine, Search, UserPlus, History, CheckCircle2, AlertTriangle, XCircle, Info, Camera, AlertOctagon, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { IncidentModal } from "../components/IncidentModal";
import { PhotoThumb } from "../components/ImageLightbox";
import { ConnectivityBadge, pushQueue } from "../lib/online";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, apiError, fileUrl } from "../lib/api";

// html5-qrcode deja promesas play() sin capturar al pausar/detener la cámara,
// lo que dispara el overlay de errores en desarrollo. Se capturan aquí (benigno).
if (typeof window !== "undefined" && !window.__hzPlayPatched) {
  window.__hzPlayPatched = true;
  const origPlay = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function (...args) {
    const p = origPlay.apply(this, args);
    if (p && typeof p.catch === "function") return p.catch(() => {});
    return p;
  };
}

const STAFF = ["admin", "welcome_coordinator", "usher", "room_manager"];
const ONSITE_ROLES = ["admin", "welcome_coordinator", "usher"];
const ROLE_LABELS = {
  admin: "Administrador",
  welcome_coordinator: "Coordinador de bienvenida",
  usher: "Ujier",
  room_manager: "Responsable de salón",
};
const CHECKPOINTS = [
  { id: "dinner", label: "Cena — Viernes" },
  { id: "general_event", label: "Evento general — Sábado" },
  { id: "workshop", label: "Taller — Sábado" },
];

const RESULT_STYLES = {
  authorized: { cls: "border-hz-success bg-hz-success/10", icon: CheckCircle2, iconCls: "text-hz-success", title: "Autorizado" },
  pending: { cls: "border-hz-warning bg-hz-warning/10", icon: AlertTriangle, iconCls: "text-hz-warning", title: "Pendiente de revisión" },
  not_authorized: { cls: "border-hz-error bg-hz-error/10", icon: XCircle, iconCls: "text-hz-error", title: "No autorizado" },
  invalid: { cls: "border-hz-error bg-hz-error/10", icon: XCircle, iconCls: "text-hz-error", title: "Código inválido" },
  already_registered: { cls: "border-hz-blue bg-hz-blue/10", icon: Info, iconCls: "text-hz-blue", title: "Acceso ya registrado" },
  error: { cls: "border-hz-error bg-hz-error/10", icon: XCircle, iconCls: "text-hz-error", title: "Error" },
};

export const ResultBanner = ({ result, onClear }) => {
  if (!result) return null;
  const s = RESULT_STYLES[result.result] || RESULT_STYLES.error;
  const Icon = s.icon;
  return (
    <div className={`rounded-2xl border-2 p-5 ${s.cls}`} data-testid="checkin-result" role="alert">
      <div className="flex items-start gap-4">
        {result.attendee?.photo_url && (
          <PhotoThumb src={result.attendee.photo_url} alt={`Fotografía de ${result.attendee.full_name}`} className="h-16 w-16 rounded-xl border border-hz-gray/50 object-cover" testId="checkin-result-photo" />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Icon className={`h-6 w-6 ${s.iconCls}`} />
            <p className="text-lg font-extrabold text-hz-ink" data-testid="checkin-result-title">{s.title}</p>
          </div>
          <p className="mt-1 text-sm font-semibold text-hz-ink" data-testid="checkin-result-message">{result.message}</p>
          {result.attendee && (
            <p className="mt-1 text-sm text-hz-ink/80">
              <span className="font-bold">{result.attendee.full_name}</span> · {result.attendee.folio}
            </p>
          )}
          {result.workshop_name && <p className="text-xs font-semibold text-hz-ink/60">Taller: {result.workshop_name}</p>}
          {result.checked_at && (
            <p className="mt-1 text-xs text-hz-ink/60">
              Registrado: {new Date(result.checked_at).toLocaleString("es-MX")}{result.usher_name ? ` · por ${result.usher_name}` : ""}
            </p>
          )}
          <Button onClick={onClear} variant="outline" size="sm" data-testid="checkin-clear-btn" className="mt-3 font-bold">
            Siguiente
          </Button>
        </div>
      </div>
    </div>
  );
};

const OnsiteForm = () => {
  const [catalog, setCatalog] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", delegation_id: "", sector_id: "", general_event: false, dinner: false, workshops: [], photo: null });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const photoRef = useRef(null);
  const photoCamRef = useRef(null);

  useEffect(() => {
    api.get("/public/catalog").then((r) => setCatalog(r.data.delegations || [])).catch(() => {});
    api.get("/public/workshops").then((r) => setWorkshops((r.data.workshops || []).filter((w) => w.schedule_complete !== false))).catch(() => {});
  }, []);

  const delegation = catalog.find((d) => d.id === form.delegation_id);
  const sectors = delegation?.sectors || [];

  const toggleWorkshop = (id) =>
    setForm((f) => {
      const adding = !f.workshops.includes(id);
      return {
        ...f,
        workshops: adding ? [...f.workshops, id] : f.workshops.filter((w) => w !== id),
        general_event: adding && !f.general_event ? true : f.general_event,
      };
    });

  const toggleGeneral = () =>
    setForm((f) => {
      if (f.general_event && (f.dinner || f.workshops.length > 0)) {
        toast.error("La cena y los talleres requieren acceso al evento general del sábado.");
        return f;
      }
      return { ...f, general_event: !f.general_event };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.full_name.trim() || !form.phone.trim() || !form.delegation_id || !form.sector_id) {
      toast.error("Completa nombre, teléfono, delegación y sector.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name.trim());
      fd.append("phone", form.phone);
      if (form.email.trim()) fd.append("email", form.email.trim());
      fd.append("delegation_id", form.delegation_id);
      fd.append("sector_id", form.sector_id);
      fd.append("general_event", (form.general_event || form.dinner || form.workshops.length > 0) ? "true" : "false");
      fd.append("dinner", form.dinner ? "true" : "false");
      fd.append("workshop_ids", form.workshops.join(","));
      if (form.photo) fd.append("photo", form.photo);
      const { data } = await api.post("/onsite/registrations", fd);
      setCreated(data);
      setForm({ full_name: "", phone: "", email: "", delegation_id: "", sector_id: "", general_event: false, dinner: false, workshops: [], photo: null });
    } catch (err) {
      toast.error(apiError(err, "No se pudo crear el registro."));
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="rounded-2xl border-2 border-hz-success bg-hz-success/5 p-6 text-center" data-testid="onsite-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-hz-success" />
        <p className="mt-3 text-lg font-bold text-hz-ink">{created.full_name}</p>
        <p className="mt-1 text-sm text-hz-ink/70">Registro en sitio creado. Entrega este folio al asistente:</p>
        <p className="mt-2 text-2xl font-extrabold tracking-wider text-hz-navy" data-testid="onsite-folio">{created.folio}</p>
        <p className="mt-2 text-xs text-hz-ink/60">El pago queda pendiente; el acceso se activará al confirmar el pago o aplicar una cortesía.</p>
        <Button onClick={() => setCreated(null)} data-testid="onsite-another-btn" className="mt-4 bg-hz-navy font-bold text-white">Registrar otra persona</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-hz-gray/40 bg-white p-5" data-testid="onsite-form">
      <div>
        <Label className="font-semibold text-hz-ink">Nombre completo *</Label>
        <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="onsite-name-input" className="mt-1" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label className="font-semibold text-hz-ink">Teléfono *</Label>
          <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="onsite-phone-input" className="mt-1" />
        </div>
        <div>
          <Label className="font-semibold text-hz-ink">Correo (opcional)</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="onsite-email-input" className="mt-1" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-semibold">Delegación *</Label>
          <Select value={form.delegation_id} onValueChange={(v) => setForm({ ...form, delegation_id: v, sector_id: "" })}>
            <SelectTrigger data-testid="onsite-select-delegation" className="mt-1"><SelectValue placeholder="Delegación" /></SelectTrigger>
            <SelectContent>{catalog.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Sector *</Label>
          <Select value={form.sector_id} disabled={!form.delegation_id} onValueChange={(v) => setForm({ ...form, sector_id: v })}>
            <SelectTrigger data-testid="onsite-select-sector" className="mt-1"><SelectValue placeholder="Sector" /></SelectTrigger>
            <SelectContent>{sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <button type="button" onClick={toggleGeneral} data-testid="onsite-general-toggle"
        className={`w-full rounded-xl border-2 p-3 text-left text-sm font-bold transition-colors ${form.general_event || form.dinner || form.workshops.length > 0 ? "border-hz-cyan bg-hz-cyan/5 text-hz-navy" : "border-hz-gray/50 text-hz-ink/70"}`}>
        {form.general_event || form.dinner || form.workshops.length > 0 ? "✓ " : ""}Evento general — sábado
      </button>
      <button type="button" onClick={() => setForm((f) => ({ ...f, dinner: !f.dinner, general_event: !f.dinner && !f.general_event ? true : f.general_event }))} data-testid="onsite-dinner-toggle"
        className={`w-full rounded-xl border-2 p-3 text-left text-sm font-bold transition-colors ${form.dinner ? "border-hz-cyan bg-hz-cyan/5 text-hz-navy" : "border-hz-gray/50 text-hz-ink/70"}`}>
        {form.dinner ? "✓ " : ""}Cena especial del viernes (opcional)
      </button>
      <div>
        <p className="text-sm font-bold text-hz-ink">Talleres (opcionales)</p>
        <div className="mt-2 space-y-2">
          {workshops.map((w) => (
            <button key={w.id} type="button" onClick={() => toggleWorkshop(w.id)} data-testid={`onsite-workshop-${w.id}`}
              className={`w-full rounded-xl border-2 p-3 text-left text-sm font-semibold transition-colors ${form.workshops.includes(w.id) ? "border-hz-cyan bg-hz-cyan/5 text-hz-navy" : "border-hz-gray/50 text-hz-ink/70"} ${w.available <= 0 && !form.workshops.includes(w.id) ? "opacity-50" : ""}`}>
              {form.workshops.includes(w.id) ? "✓ " : ""}{w.name} · {w.available}/{w.capacity} lugares
            </button>
          ))}
        </div>
      </div>
      <div>
        <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="onsite-photo-input" onChange={(e) => setForm({ ...form, photo: e.target.files?.[0] || null })} />
        <input ref={photoCamRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="onsite-photo-camera-input" onChange={(e) => setForm({ ...form, photo: e.target.files?.[0] || null })} />
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" onClick={() => photoCamRef.current?.click()} data-testid="onsite-photo-camera-btn" className="font-semibold">
            <Camera className="mr-2 h-4 w-4" /> Tomar foto
          </Button>
          <Button type="button" variant="outline" onClick={() => photoRef.current?.click()} data-testid="onsite-photo-btn" className="font-semibold">
            {form.photo ? form.photo.name : "Subir desde dispositivo"}
          </Button>
        </div>
        <p className="mt-1 text-xs text-hz-ink/50">Fotografía opcional. En algunos dispositivos, la cámara puede abrirse mediante el selector del sistema.</p>
      </div>
      <Button type="submit" disabled={submitting} data-testid="onsite-submit-btn" className="w-full bg-hz-cyan font-bold text-white hover:bg-hz-blue disabled:opacity-60">
        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Crear registro en sitio
      </Button>
    </form>
  );
};

const CheckinPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [checkpoint, setCheckpoint] = useState("general_event");
  const [workshopId, setWorkshopId] = useState("");
  const [workshops, setWorkshops] = useState([]);
  const [tab, setTab] = useState("scan");
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [logs, setLogs] = useState([]);
  const lastScanRef = useRef(0);
  const [incidentOpen, setIncidentOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("hz_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    api.get("/public/workshops").then((r) => {
      setWorkshops((r.data.workshops || []).filter((w) => w.schedule_complete !== false));
      if (r.data.workshops?.length) setWorkshopId(r.data.workshops[0].id);
    }).catch(() => {});
  }, []);

  const validate = useCallback(async ({ token, attendee_id }) => {
    if (validating) return;
    setValidating(true);
    try {
      const { data } = await api.post("/checkin/validate", {
        token: token || null,
        attendee_id: attendee_id || null,
        checkpoint_type: checkpoint,
        workshop_id: checkpoint === "workshop" ? workshopId : null,
      });
      setResult(data);
    } catch (err) {
      if (!err?.response) {
        pushQueue({
          token: token || null, attendee_id: attendee_id || null, checkpoint_type: checkpoint,
          workshop_id: checkpoint === "workshop" ? workshopId : null, manual: false, reason: null,
        });
        setResult({ result: "pending", message: "Sin conexión: la operación quedó en cola y se sincronizará automáticamente al recuperar internet.", attendee: null });
      } else {
        setResult({ result: "error", message: apiError(err, "No se pudo validar el acceso.") });
      }
    } finally {
      setValidating(false);
    }
  }, [checkpoint, workshopId, validating]);

  // Escáner de cámara
  useEffect(() => {
    if (tab !== "scan" || !user || !STAFF.includes(user.role)) return;
    const scanner = new Html5Qrcode("qr-reader");
    let active = true;
    let started = false;
    setCameraError(null);
    scanner
      .start(
        { facingMode: "environment" },
        { fps: 8, qrbox: { width: 220, height: 220 } },
        (text) => {
          const now = Date.now();
          if (!active || now - lastScanRef.current < 3000) return;
          lastScanRef.current = now;
          validate({ token: text });
        },
        () => {}
      )
      .then(() => { started = true; })
      .catch(() => { if (active) setCameraError("No se pudo acceder a la cámara. Usa la búsqueda manual."); });
    return () => {
      active = false;
      if (!started) return;
      try {
        scanner.stop().then(() => scanner.clear()).catch(() => {});
      } catch (e) {
        // el escáner no llegó a iniciarse
      }
    };
  }, [tab, user, validate]);

  const search = async (e) => {
    e?.preventDefault();
    if (query.trim().length < 2 || searching) return;
    setSearching(true);
    try {
      const { data } = await api.get("/checkin/search", { params: { q: query.trim() } });
      setResults(data.results || []);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSearching(false);
    }
  };

  const loadLogs = useCallback(() => {
    api.get("/checkin/logs").then((r) => setLogs(r.data.logs || [])).catch(() => {});
  }, []);

  useEffect(() => { if (tab === "history") loadLogs(); }, [tab, loadLogs]);

  const logout = () => {
    localStorage.removeItem("hz_admin_token");
    navigate("/login");
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-hz-bg text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  if (!user) return <Navigate to="/login?redirect=/checkin" replace />;

  if (!STAFF.includes(user.role)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-hz-bg px-4 text-center">
        <XCircle className="h-12 w-12 text-hz-error" />
        <p className="mt-4 font-bold text-hz-navy">Tu usuario no tiene permisos de check-in.</p>
        <Button onClick={logout} variant="outline" className="mt-4 font-bold" data-testid="checkin-relogin-btn">Cambiar de usuario</Button>
      </div>
    );
  }

  const accessBadge = (status) => {
    if (!status) return <span className="rounded-full bg-hz-gray/30 px-2 py-0.5 text-[10px] font-bold text-hz-ink/60">Sin acceso</span>;
    const map = { active: ["Autorizado", "bg-hz-success/10 text-hz-success"], pending: ["Pendiente", "bg-hz-warning/10 text-hz-warning"], revoked: ["Revocado", "bg-hz-error/10 text-hz-error"] };
    const [label, cls] = map[status] || [status, "bg-hz-gray/30 text-hz-ink"];
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}>{label}</span>;
  };

  return (
    <div className="min-h-screen bg-hz-bg" data-testid="checkin-page">
      <header className="border-b border-hz-gray/50 bg-white">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-2 px-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Logo variant="title" className="h-8 shrink-0" />
            <span className="hidden shrink-0 whitespace-nowrap rounded-full bg-hz-cyan/10 px-3 py-1 text-xs font-bold text-hz-blue sm:inline">Check-in</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ConnectivityBadge compact />
            <span className="hidden text-xs font-medium text-hz-ink/70 sm:block">{user.name} · {ROLE_LABELS[user.role]}</span>
            <Button variant="outline" size="sm" onClick={() => setIncidentOpen(true)} data-testid="checkin-incident-btn" className="font-semibold text-hz-warning" aria-label="Reportar incidencia"><AlertOctagon className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/contingencia")} data-testid="checkin-contingencia-btn" className="font-semibold text-hz-navy" aria-label="Protocolo de contingencia"><LifeBuoy className="h-4 w-4" /></Button>
            {user.role === "admin" && (
              <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="checkin-admin-btn" className="font-semibold">Admin</Button>
            )}
            <Button variant="outline" size="sm" onClick={logout} data-testid="checkin-logout-btn" className="font-semibold text-hz-error">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6">
        {/* Punto de control */}
        <div className="rounded-2xl border border-hz-gray/40 bg-white p-4" data-testid="checkpoint-selector">
          <p className="text-xs font-bold uppercase tracking-widest text-hz-blue">Punto de control</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {CHECKPOINTS.filter((c) => user.role !== "room_manager" || c.id === "workshop").map((c) => (
              <button
                key={c.id}
                onClick={() => { setCheckpoint(c.id); setResult(null); }}
                data-testid={`checkpoint-${c.id}`}
                className={`rounded-xl border-2 px-2 py-3 text-xs font-bold transition-colors sm:text-sm ${checkpoint === c.id ? "border-hz-cyan bg-hz-cyan/10 text-hz-navy" : "border-hz-gray/40 text-hz-ink/60"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {checkpoint === "workshop" && (
            <Select value={workshopId} onValueChange={(v) => { setWorkshopId(v); setResult(null); }}>
              <SelectTrigger data-testid="checkpoint-workshop-select" className="mt-3"><SelectValue placeholder="Selecciona el taller" /></SelectTrigger>
              <SelectContent>
                {workshops.map((w) => (
                  <SelectItem key={w.id} value={w.id} data-testid={`checkpoint-workshop-${w.id}`}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="mt-4">
          <ResultBanner result={result} onClear={() => setResult(null)} />
          {validating && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-hz-navy" data-testid="checkin-validating">
              <Loader2 className="h-4 w-4 animate-spin" /> Validando acceso…
            </div>
          )}
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setResult(null); }} className="mt-4">
          <TabsList className="grid h-auto w-full grid-cols-4 bg-white p-1">
            <TabsTrigger value="scan" data-testid="checkin-tab-scan" className="text-xs font-bold sm:text-sm"><ScanLine className="mr-1 h-4 w-4" />Escanear</TabsTrigger>
            <TabsTrigger value="search" data-testid="checkin-tab-search" className="text-xs font-bold sm:text-sm"><Search className="mr-1 h-4 w-4" />Buscar</TabsTrigger>
            {ONSITE_ROLES.includes(user.role) ? (
              <TabsTrigger value="onsite" data-testid="checkin-tab-onsite" className="text-xs font-bold sm:text-sm"><UserPlus className="mr-1 h-4 w-4" />En sitio</TabsTrigger>
            ) : (
              <TabsTrigger value="none" disabled className="text-xs font-bold sm:text-sm"><UserPlus className="mr-1 h-4 w-4" />En sitio</TabsTrigger>
            )}
            <TabsTrigger value="history" data-testid="checkin-tab-history" className="text-xs font-bold sm:text-sm"><History className="mr-1 h-4 w-4" />Historial</TabsTrigger>
          </TabsList>

          <TabsContent value="scan" className="mt-4">
            <div className="rounded-2xl border border-hz-gray/40 bg-white p-4">
              {cameraError ? (
                <p className="py-8 text-center text-sm font-semibold text-hz-warning" data-testid="camera-error">{cameraError}</p>
              ) : (
                <div id="qr-reader" className="mx-auto w-full max-w-sm overflow-hidden rounded-xl" data-testid="qr-reader" />
              )}
              <p className="mt-3 text-center text-xs text-hz-ink/60">
                Apunta la cámara al código QR del pase digital del asistente.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="search" className="mt-4">
            <form onSubmit={search} className="flex gap-2">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nombre, folio, correo o teléfono" data-testid="checkin-search-input" />
              <Button type="submit" disabled={searching} data-testid="checkin-search-btn" className="bg-hz-navy font-bold text-white">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </form>
            <div className="mt-4 space-y-3">
              {results.map((r) => (
                <div key={r.id} className="flex items-start gap-3 rounded-2xl border border-hz-gray/40 bg-white p-4" data-testid={`search-result-${r.folio}`}>
                  {r.photo_url && <img src={fileUrl(r.photo_url)} alt={`Fotografía de ${r.full_name}`} className="h-12 w-12 rounded-lg border border-hz-gray/50 object-cover" />}
                  <div className="flex-1">
                    <p className="text-sm font-bold text-hz-ink">{r.full_name}</p>
                    <p className="text-xs font-semibold text-hz-navy">{r.folio}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-semibold text-hz-ink/50">General:</span>{accessBadge(r.access.general_event)}
                      <span className="text-[10px] font-semibold text-hz-ink/50">Cena:</span>{accessBadge(r.access.dinner)}
                      {r.access.workshops.map((w) => (
                        <span key={w.workshop_id} className="inline-flex items-center gap-1">
                          <span className="text-[10px] font-semibold text-hz-ink/50">{w.name}:</span>{accessBadge(w.status)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => validate({ attendee_id: r.id })} disabled={validating} data-testid={`search-validate-${r.folio}`} className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
                    Validar
                  </Button>
                </div>
              ))}
              {results.length === 0 && query.length >= 2 && !searching && (
                <p className="py-4 text-center text-sm text-hz-ink/60" data-testid="search-empty">Sin resultados.</p>
              )}
            </div>
          </TabsContent>

          {ONSITE_ROLES.includes(user.role) && (
            <TabsContent value="onsite" className="mt-4">
              <OnsiteForm />
            </TabsContent>
          )}

          <TabsContent value="history" className="mt-4">
            <div className="rounded-2xl border border-hz-gray/40 bg-white" data-testid="checkin-history">
              <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Últimos registros de acceso</p>
              {logs.length === 0 ? (
                <p className="px-4 py-6 text-sm text-hz-ink/60" data-testid="history-empty">Aún no hay registros de check-in.</p>
              ) : (
                <div className="divide-y divide-hz-gray/30">
                  {logs.map((l) => (
                    <div key={l.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`history-row-${l.id}`}>
                      <div>
                        <p className="text-sm font-bold text-hz-ink">{l.attendee_name}</p>
                        <p className="text-xs text-hz-ink/60">
                          {l.folio} · {CHECKPOINTS.find((c) => c.id === l.checkpoint_type)?.label}{l.workshop_name ? ` · ${l.workshop_name}` : ""}
                        </p>
                      </div>
                      <div className="text-right text-xs text-hz-ink/60">
                        <p>{new Date(l.created_at).toLocaleString("es-MX")}</p>
                        <p>{l.usher_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <IncidentModal open={incidentOpen} onClose={() => setIncidentOpen(false)} workshopId={checkpoint === "workshop" ? workshopId : null} />
    </div>
  );
};

export default CheckinPage;
